import React from 'react';
import { QrCode, CheckCircle, Package, XCircle, Users, TrendingUp } from 'lucide-react';
import Icon from '../../../components/AppIcon';


interface QRCodeKPICardsProps {
  totalCodes: number;
  usedCodes: number;
  availableCodes: number;
  expiredCodes: number;
  activatedUsers: number;
  activationRate: number;
}

const QRCodeKPICards: React.FC<QRCodeKPICardsProps> = ({
  totalCodes,
  usedCodes,
  availableCodes,
  expiredCodes,
  activatedUsers,
  activationRate
}) => {
  const kpiData = [
    {
      title: 'Total QR Codes',
      value: totalCodes.toLocaleString(),
      icon: QrCode,
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600',
      description: 'All activation codes'
    },
    {
      title: 'Used Codes',
      value: usedCodes.toLocaleString(),
      icon: CheckCircle,
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600',
      description: 'Successfully redeemed',
      percentage: totalCodes > 0 ? ((usedCodes / totalCodes) * 100).toFixed(1) : '0'
    },
    {
      title: 'Available Codes',
      value: availableCodes.toLocaleString(),
      icon: Package,
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600',
      description: 'Ready for activation',
      percentage: totalCodes > 0 ? ((availableCodes / totalCodes) * 100).toFixed(1) : '0'
    },
    {
      title: 'Expired Codes',
      value: expiredCodes.toLocaleString(),
      icon: XCircle,
      bgColor: 'bg-orange-50',
      iconColor: 'text-orange-600',
      description: 'Past expiration date'
    },
    {
      title: 'Activated Users',
      value: activatedUsers.toLocaleString(),
      icon: Users,
      bgColor: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      description: 'Users with redemption access'
    },
    {
      title: 'Activation Rate',
      value: `${activationRate.toFixed(1)}%`,
      icon: TrendingUp,
      bgColor: 'bg-cyan-50',
      iconColor: 'text-cyan-600',
      description: 'User activation percentage'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
      {kpiData.map((kpi) => {
        const Icon = kpi.icon;
        return (
          <div 
            key={kpi.title}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`${kpi.bgColor} p-3 rounded-lg`}>
                <Icon className={`h-6 w-6 ${kpi.iconColor}`} />
              </div>
            </div>
            
            <h3 className="text-sm font-medium text-gray-600 mb-1">
              {kpi.title}
            </h3>
            
            <div className="flex items-baseline space-x-2">
              <p className="text-2xl font-bold text-gray-900">
                {kpi.value}
              </p>
              {kpi.percentage && (
                <span className="text-sm text-gray-500">
                  ({kpi.percentage}%)
                </span>
              )}
            </div>
            
            <p className="text-xs text-gray-500 mt-2">
              {kpi.description}
            </p>
          </div>
        );
      })}
    </div>
  );
};

export default QRCodeKPICards;